import { loadCachedOrgSettings } from "./orgSettingsBridge";

export const GUEST_DEMO_SAVE_MESSAGE =
  "Demo Mode — Sign in to save your work.";

export const GUEST_SESSION_KEY = "CP_GUEST_SESSION";

/**
 * Capabilities granted during the guest application tour (navigation + view only).
 * String keys match CAPABILITY values in permissions.js (no import — avoids cycles).
 */
export const GUEST_TOUR_CAPABILITIES = new Set([
  "screen.mode_select",
  "screen.kpi_dashboard",
  "screen.time_tracking",
  "screen.cockpit",
  "screen.manual_time_entry",
  "screen.create_client",
  "screen.create_project",
  "screen.project_detail",
  "project.detail.operational",
  "project.view",
  "project.create",
  "client.create",
  "screen.intelligence",
  "report.builder.view",
  "project.analytics.view",
  "screen.payroll_home",
  "payroll.queue",
  "payroll.report",
  "payroll.export",
  "screen.materials",
  "material.catalog.search",
  "screen.estimate_hub",
  "estimate.view",
  "screen.invoice_hub",
  "invoice.view",
  "screen.accounts_receivable",
  "accounts_receivable.view",
  "screen.mission_control",
  "mission_control.operational.view",
  "dashboard.active_crew",
  "accounts_payable.view",
  "timelog.view",
]);

export function isGuestActor(actor) {
  return actor?.isGuest === true;
}

export function isGuestTourCapability(capability) {
  return GUEST_TOUR_CAPABILITIES.has(capability);
}

export function guestDemoGrant() {
  return {
    allowed: true,
    scope: "organization",
    visibility: "edit",
  };
}

export function isGuestModeAllowed(orgSettings) {
  const settings =
    orgSettings && typeof orgSettings === "object"
      ? orgSettings
      : loadCachedOrgSettings();

  if (settings.guest_mode_enabled === false) {
    return false;
  }
  return true;
}

/**
 * Whether the onboarding guest/demo entry should be shown.
 * Production builds require REACT_APP_ALLOW_GUEST=true unless org policy disables guest mode.
 */
export function isGuestEntryAllowed(orgSettings) {
  const envAllow = process.env.REACT_APP_ALLOW_GUEST === "true";

  if (process.env.NODE_ENV === "production" && !envAllow) {
    return false;
  }

  return isGuestModeAllowed(orgSettings);
}

export function createGuestUser() {
  return {
    employee_name: "Guest User",
    role: "guest",
    isGuest: true,
    supabase_user_id: null,
  };
}

export function persistGuestSession(user) {
  try {
    sessionStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(user));
  } catch {
    // ignore quota errors
  }
}

export function readGuestSession() {
  try {
    const raw = sessionStorage.getItem(GUEST_SESSION_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed?.isGuest ? parsed : null;
  } catch {
    return null;
  }
}

export function clearGuestSession() {
  try {
    sessionStorage.removeItem(GUEST_SESSION_KEY);
  } catch {
    // ignore
  }
}

export function shouldSkipSupabase(actor) {
  return isGuestActor(actor);
}

/**
 * Returns true when the action was blocked (caller should return early).
 */
export function blockDemoSave(actor) {
  const guest =
    actor && isGuestActor(actor) ? actor : readGuestSession();
  if (!isGuestActor(guest)) {
    return false;
  }
  window.alert(GUEST_DEMO_SAVE_MESSAGE);
  return true;
}
